import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, User, Users, Flag, X, Check, Building2, Briefcase, Filter, ChevronDown, ChevronUp, FileSpreadsheet } from 'lucide-react';

const CARD_ICONS = {
  fire: { emoji: '🔥', label: 'Fire' },
  planned: { emoji: '📋', label: 'Planned' },
  important: { emoji: '⚠️', label: 'Important' }
};

const priorityColors = {
  low: { bg: '#dcfce7', color: '#16a34a', label: 'Low' },
  medium: { bg: '#fef3c7', color: '#d97706', label: 'Medium' },
  high: { bg: '#fee2e2', color: '#dc2626', label: 'High' }
};

function Planner({ user, onOpenSheet, workspaceId }) {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [clients, setClients] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Return modal state
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnItem, setReturnItem] = useState(null);
  const [returnObservations, setReturnObservations] = useState('');
  
  // Filter state
  const [filterAgency, setFilterAgency] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterStakeholder, setFilterStakeholder] = useState('');
  const [filterTeamLead, setFilterTeamLead] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  const canEdit = user?.can_edit_planner || user?.is_admin;
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    stakeholder_id: '',
    team_lead_id: '',
    agency_id: '',
    people_client_id: '',
    priority: 'medium',
    status: 'backlog',
    sheet_id: '',
    icon: '',
    send_to_workspace_id: ''
  });

  const loadData = useCallback(async () => {
    try {
      const [itemsRes, contactsRes, agenciesRes, clientsRes, workspacesRes] = await Promise.all([
        axios.get('/api/backlog'),
        axios.get('/api/contacts'),
        axios.get('/api/agencies'),
        axios.get('/api/people-clients'),
        axios.get('/api/workspaces')
      ]);
      setItems(itemsRes.data || []);
      setContacts(contactsRes.data || []);
      setAgencies(agenciesRes.data || []);
      setClients(clientsRes.data || []);
      setWorkspaces(workspacesRes.data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
    // Load sheets separately so a failure doesn't break the planner
    try {
      const sheetsRes = await axios.get('/api/sheets');
      setSheets(Array.isArray(sheetsRes.data) ? sheetsRes.data : (sheetsRes.data?.sheets || []));
    } catch (err) {
      console.error('Failed to load sheets:', err);
      setSheets([]);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData, workspaceId]);

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      stakeholder_id: '',
      team_lead_id: '',
      agency_id: '',
      people_client_id: '',
      priority: 'medium',
      status: 'backlog',
      sheet_id: '',
      icon: '',
      send_to_workspace_id: ''
    });
    setEditingItem(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;

    try {
      let savedItem;
      if (editingItem) {
        const res = await axios.put(`/api/backlog/${editingItem.id}`, form);
        savedItem = res.data || editingItem;
        // Link or unlink sheet
        await axios.put(`/api/backlog/${editingItem.id}/link-sheet`, { sheet_id: form.sheet_id || null });
      } else {
        const res = await axios.post('/api/backlog', form);
        savedItem = res.data;
        // Link sheet if selected for a new item
        if (savedItem?.id && form.sheet_id) {
          await axios.put(`/api/backlog/${savedItem.id}/link-sheet`, { sheet_id: form.sheet_id });
        }
      }
      loadData();
      resetForm();
    } catch (err) {
      console.error('Failed to save item:', err);
      alert('Failed to save item');
    }
  };

  const handleEdit = (item) => {
    setForm({
      title: item.title,
      description: item.description || '',
      stakeholder_id: item.stakeholder_id || '',
      team_lead_id: item.team_lead_id || '',
      agency_id: item.agency_id || '',
      people_client_id: item.people_client_id || '',
      priority: item.priority || 'medium',
      status: item.status || 'backlog',
      sheet_id: item.sheet_id || '',
      icon: item.icon || '',
      send_to_workspace_id: ''
    });
    setEditingItem(item);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`/api/backlog/${id}`);
      loadData();
    } catch (err) {
      console.error('Failed to delete item:', err);
      alert('Failed to delete item');
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await axios.put(`/api/backlog/${item.id}`, { ...item, status: newStatus });
      loadData();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleReturn = async (itemId, returnStatus, observations) => {
    try {
      await axios.post(`/api/backlog/${itemId}/return`, {
        return_status: returnStatus,
        observations: observations
      });
      loadData();
      setShowReturnModal(false);
      setReturnItem(null);
      setReturnObservations('');
    } catch (err) {
      console.error('Failed to return item:', err);
      alert(err.response?.data?.error || 'Failed to return item');
    }
  };

  // Filter items by agency, client, stakeholder, and team lead
  const filteredItems = items.filter(item => {
    if (filterAgency && item.agency_id !== parseInt(filterAgency)) return false;
    if (filterClient && item.people_client_id !== parseInt(filterClient)) return false;
    if (filterStakeholder && item.stakeholder_id !== filterStakeholder) return false;
    if (filterTeamLead && item.team_lead_id !== filterTeamLead) return false;
    return true;
  });

  // Separate items by status
  const awaitingExecutionItems = filteredItems.filter(item => item.status === 'awaiting_execution');
  const requestedItems = filteredItems.filter(item => item.status === 'requested');
  const ongoingItems = filteredItems.filter(item => item.status === 'in_progress');
  const parkingLotItems = filteredItems.filter(item => item.status === 'backlog');
  const doneItems = filteredItems.filter(item => item.status === 'done');

  const sortedContacts = [...contacts].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const sortedAgencies = [...agencies].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const sortedClients = [...clients].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const hasActiveFilters = filterAgency || filterClient || filterStakeholder || filterTeamLead;
  const activeFilterCount = [filterAgency, filterClient, filterStakeholder, filterTeamLead].filter(Boolean).length;

  // Card component - compact square design
  const ItemCard = ({ item, index }) => {
    const isChild = item.parent_card_id != null;
    const isReturned = isChild && item.transfer_return_status != null;
    const isParent = item.children && item.children.length > 0;
    const isTransferred = !isChild && (item.status === 'awaiting_execution' || item.source_workspace_id !== null || item.target_workspace_id !== null);

    const cardBg = isChild 
      ? (isReturned ? '#f8fafc' : '#fffbeb') 
      : (isTransferred ? '#faf5ff' : 'white');
      
    const cardBorderLeft = isChild
      ? (isReturned ? '4px solid #cbd5e1' : '4px solid #f59e0b')
      : (item.status === 'requested' ? '4px solid #f59e0b' : isTransferred ? '4px solid #8b5cf6' : '1px solid #e2e8f0');

    const cardOpacity = isReturned ? 0.7 : 1;

    // Children counting for parent card
    const totalChildren = item.children ? item.children.length : 0;
    const doneCount = item.children ? item.children.filter(c => c.transfer_return_status !== null).length : 0;
    const pendingCount = totalChildren - doneCount;
    const obsCount = item.children ? item.children.filter(c => c.transfer_return_status === 'returned_with_observations').length : 0;

    return (
      <div
        style={{
          background: cardBg,
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          borderLeft: cardBorderLeft,
          padding: '16px',
          width: '210px',
          height: '210px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          opacity: cardOpacity
        }}
      >
        {/* Header: Number + Code + Priority */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: priorityColors[item.priority]?.bg || '#f1f5f9',
            color: priorityColors[item.priority]?.color || '#64748b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '700',
            fontSize: '0.75rem'
          }}>
            {index + 1}
          </div>
          {item.card_code && (
            <span style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: '#94a3b8',
              fontFamily: 'monospace'
            }}>
              {item.card_code}
            </span>
          )}
          <span style={{
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.6rem',
            fontWeight: '600',
            background: priorityColors[item.priority]?.bg,
            color: priorityColors[item.priority]?.color,
            textTransform: 'uppercase'
          }}>
            {item.priority}
          </span>
        </div>

        {/* Title with icon */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', margin: '0 0 6px' }}>
          {item.icon && CARD_ICONS[item.icon] && (
            <span style={{ fontSize: '1rem', flexShrink: 0, lineHeight: 1 }}>
              {CARD_ICONS[item.icon].emoji}
            </span>
          )}
          <h4 style={{
            margin: 0,
            fontSize: '0.85rem',
            fontWeight: '600',
            color: '#1a1a2e',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.3'
          }}>
            {item.title}
          </h4>
        </div>

        {/* Parent Observations List if any resolved with notes */}
        {isParent && item.children && item.children.some(c => c.transfer_observations) && (
          <div style={{
            marginBottom: '6px',
            maxHeight: '42px',
            overflowY: 'auto',
            fontSize: '0.6rem',
            color: '#dc2626',
            background: '#fee2e2',
            padding: '4px 6px',
            borderRadius: '6px',
            borderLeft: '2px solid #ef4444'
          }}>
            <strong>Notes:</strong>
            {item.children.filter(c => c.transfer_observations).map((c, i) => (
              <div key={i} style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                • {c.workspace_name}: {c.transfer_observations}
              </div>
            ))}
          </div>
        )}

        {/* Child Observations List if this child card has notes */}
        {isChild && item.transfer_observations && (
          <div style={{
            marginBottom: '6px',
            maxHeight: '42px',
            overflowY: 'auto',
            fontSize: '0.6rem',
            color: '#d97706',
            background: '#fef3c7',
            padding: '4px 6px',
            borderRadius: '6px',
            borderLeft: '2px solid #f59e0b'
          }}>
            <strong>Notes:</strong> {item.transfer_observations}
          </div>
        )}

        {/* Owner/Stakeholder/Agency/Client */}
        <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '8px', flex: 1, overflow: 'hidden' }}>
          {item.team_lead_name && <div>👤 {item.team_lead_name}</div>}
          {item.stakeholder_name && <div>🏢 {item.stakeholder_name}</div>}
          {item.agency_name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Briefcase size={10} /> {item.agency_name}
            </div>
          )}
          {item.client_name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Building2 size={10} /> {item.client_name}
            </div>
          )}
          {isChild && item.source_workspace_name && (
            <div style={{
              marginTop: '4px',
              fontSize: '0.65rem',
              fontWeight: '700',
              color: '#d97706',
              background: '#fef3c7',
              padding: '2px 6px',
              borderRadius: '4px',
              display: 'inline-block',
              width: 'fit-content'
            }}>
              📥 From: {item.source_workspace_name}
            </div>
          )}
          {isParent && (
            <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <div style={{
                fontWeight: '700',
                color: '#7c3aed',
                background: '#f3e8ff',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-block',
                width: 'fit-content'
              }}>
                📤 {totalChildren} team{totalChildren !== 1 ? 's' : ''} · ✅ {doneCount} · ⏳ {pendingCount}
              </div>
              {obsCount > 0 && (
                <div style={{
                  fontWeight: '700',
                  color: '#dc2626',
                  background: '#fee2e2',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  display: 'inline-block',
                  width: 'fit-content'
                }}>
                  📝 {obsCount} with notes
                </div>
              )}
            </div>
          )}
          {!isChild && !isParent && item.target_workspace_name && (
            <div style={{
              marginTop: '4px',
              fontSize: '0.65rem',
              fontWeight: '700',
              color: '#7c3aed',
              background: '#f3e8ff',
              padding: '2px 6px',
              borderRadius: '4px',
              display: 'inline-block',
              width: 'fit-content'
            }}>
              📤 Sent to: {item.target_workspace_name}
            </div>
          )}
        </div>

        {/* Actions row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', gap: '4px' }}>
          {isChild ? (
            isReturned ? (
              <span style={{
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '0.7rem',
                fontWeight: '600',
                background: item.transfer_return_status === 'completed' ? '#dcfce7' : '#fee2e2',
                color: item.transfer_return_status === 'completed' ? '#16a34a' : '#dc2626'
              }}>
                {item.transfer_return_status === 'completed' ? 'Returned ✅' : 'Returned w/ Notes 📝'}
              </span>
            ) : canEdit ? (
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to mark this card as complete and return it to the source team?')) {
                      handleReturn(item.id, 'completed');
                    }
                  }}
                  title="Complete & Return"
                  style={{
                    padding: '4px 6px',
                    borderRadius: '6px',
                    border: '1px solid #16a34a',
                    fontSize: '0.65rem',
                    fontWeight: '600',
                    background: '#dcfce7',
                    color: '#16a34a',
                    cursor: 'pointer'
                  }}
                >
                  Complete
                </button>
                <button
                  onClick={() => {
                    setReturnItem(item);
                    setReturnObservations('');
                    setShowReturnModal(true);
                  }}
                  title="Return with Notes"
                  style={{
                    padding: '4px 6px',
                    borderRadius: '6px',
                    border: '1px solid #d97706',
                    fontSize: '0.65rem',
                    fontWeight: '600',
                    background: '#fef3c7',
                    color: '#d97706',
                    cursor: 'pointer'
                  }}
                >
                  Notes
                </button>
              </div>
            ) : (
              <span style={{
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: '500',
                background: '#fffbeb',
                color: '#d97706'
              }}>
                Pending
              </span>
            )
          ) : isParent ? (
            <span style={{
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: '500',
              background: '#f3e8ff',
              color: '#7c3aed'
            }}>
              Awaiting Execution
            </span>
          ) : canEdit ? (
            <select
              value={item.status}
              onChange={(e) => handleStatusChange(item, e.target.value)}
              style={{
                padding: '4px 8px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                fontSize: '0.75rem',
                fontWeight: '500',
                background: item.status === 'requested' ? '#fef3c7' : item.status === 'in_progress' ? '#dbeafe' : item.status === 'done' ? '#dcfce7' : '#f1f5f9',
                color: item.status === 'requested' ? '#92400e' : item.status === 'in_progress' ? '#2563eb' : item.status === 'done' ? '#16a34a' : '#64748b',
                cursor: 'pointer'
              }}
            >
              <option value="requested">Requested</option>
              <option value="backlog">Parking lot</option>
              <option value="in_progress">On going</option>
              <option value="done">Done</option>
            </select>
          ) : (
            <span style={{
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: '500',
              background: item.status === 'requested' ? '#fef3c7' : item.status === 'in_progress' ? '#dbeafe' : item.status === 'done' ? '#dcfce7' : '#f1f5f9',
              color: item.status === 'requested' ? '#92400e' : item.status === 'in_progress' ? '#2563eb' : item.status === 'done' ? '#16a34a' : '#64748b'
            }}>
              {item.status === 'requested' ? 'Requested' : item.status === 'in_progress' ? 'On going' : item.status === 'done' ? 'Done' : 'Parking lot'}
            </span>
          )}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {item.sheet_id && (() => {
              const linkedSheet = sheets.find(s => String(s.id) === String(item.sheet_id));
              return linkedSheet && onOpenSheet ? (
                <button
                  onClick={() => onOpenSheet(item.sheet_id)}
                  title={`Sheet: ${linkedSheet.name} by ${linkedSheet.creator_name || 'Unknown'}`}
                  style={{
                    padding: '6px',
                    background: '#f0f4ff',
                    border: '1px solid #c7d2fe',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: '#4f46e5'
                  }}
                >
                  <FileSpreadsheet size={12} />
                </button>
              ) : null;
            })()}
            {canEdit && (
              <>
                <button
                  onClick={() => handleEdit(item)}
                  style={{
                    padding: '6px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: '#64748b'
                  }}
                >
                  <Edit2 size={12} />
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  style={{
                    padding: '6px',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: '#dc2626'
                  }}
                >
                  <Trash2 size={12} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#64748b' }}>
        Loading...
      </div>
    );
  }

  return (
    <div data-fullwidth="true" style={{ height: 'calc(100vh - 140px)', background: '#fafbfc', display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* Sticky Header */}
      <div style={{ 
        padding: '20px 48px 16px', 
        background: '#fafbfc', 
        borderBottom: '1px solid #e8ecf1',
        flexShrink: 0
      }}>
        {/* Top row: Title + Filter toggle + Add button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
          <h1 style={{ 
            margin: 0, 
            fontSize: '1.75rem', 
            fontWeight: '600', 
            color: '#1a1a2e',
            fontStyle: 'italic'
          }}>
            Planner
          </h1>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Filter toggle button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                background: hasActiveFilters ? '#667eea' : 'white',
                color: hasActiveFilters ? 'white' : '#64748b',
                border: hasActiveFilters ? 'none' : '1px solid #e2e8f0',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <Filter size={14} />
              Filters
              {activeFilterCount > 0 && (
                <span style={{
                  background: hasActiveFilters ? 'rgba(255,255,255,0.3)' : '#667eea',
                  color: hasActiveFilters ? 'white' : 'white',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>
                  {activeFilterCount}
                </span>
              )}
              {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {canEdit && (
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '24px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <Plus size={18} />
                Add Item
              </button>
            )}
          </div>
        </div>

        {/* Collapsible Filter Panel */}
        {showFilters && (
          <div style={{
            marginTop: '16px',
            padding: '16px 20px',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
              {/* Agency Filter */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>
                  <Briefcase size={12} /> Agency
                </label>
                <select
                  value={filterAgency}
                  onChange={(e) => setFilterAgency(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: filterAgency ? '2px solid #667eea' : '1px solid #e2e8f0',
                    fontSize: '0.85rem',
                    background: filterAgency ? '#f0f4ff' : 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Agencies</option>
                  {sortedAgencies.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>

              {/* Client Filter */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>
                  <Building2 size={12} /> Client
                </label>
                <select
                  value={filterClient}
                  onChange={(e) => setFilterClient(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: filterClient ? '2px solid #667eea' : '1px solid #e2e8f0',
                    fontSize: '0.85rem',
                    background: filterClient ? '#f0f4ff' : 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Clients</option>
                  {sortedClients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Stakeholder Filter */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>
                  <User size={12} /> Stakeholder
                </label>
                <select
                  value={filterStakeholder}
                  onChange={(e) => setFilterStakeholder(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: filterStakeholder ? '2px solid #667eea' : '1px solid #e2e8f0',
                    fontSize: '0.85rem',
                    background: filterStakeholder ? '#f0f4ff' : 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Stakeholders</option>
                  {sortedContacts.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Team Lead Filter */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>
                  <Users size={12} /> Team Lead
                </label>
                <select
                  value={filterTeamLead}
                  onChange={(e) => setFilterTeamLead(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: filterTeamLead ? '2px solid #667eea' : '1px solid #e2e8f0',
                    fontSize: '0.85rem',
                    background: filterTeamLead ? '#f0f4ff' : 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">All Team Leads</option>
                  {sortedContacts.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Clear filters button */}
            {hasActiveFilters && (
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setFilterAgency(''); setFilterClient(''); setFilterStakeholder(''); setFilterTeamLead(''); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    background: '#fee2e2',
                    color: '#dc2626',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  <X size={14} />
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Active filters summary (when panel is collapsed) */}
        {!showFilters && hasActiveFilters && (
          <div style={{ 
            marginTop: '12px', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Filtering by:</span>
            {filterAgency && (
              <span style={{ 
                padding: '4px 10px', 
                background: '#f0f4ff', 
                borderRadius: '12px', 
                fontSize: '0.75rem',
                color: '#667eea',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <Briefcase size={10} />
                {sortedAgencies.find(a => a.id === parseInt(filterAgency))?.name}
              </span>
            )}
            {filterClient && (
              <span style={{ 
                padding: '4px 10px', 
                background: '#f0f4ff', 
                borderRadius: '12px', 
                fontSize: '0.75rem',
                color: '#667eea',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <Building2 size={10} />
                {sortedClients.find(c => c.id === parseInt(filterClient))?.name}
              </span>
            )}
            {filterStakeholder && (
              <span style={{ 
                padding: '4px 10px', 
                background: '#f0f4ff', 
                borderRadius: '12px', 
                fontSize: '0.75rem',
                color: '#667eea',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <User size={10} />
                {sortedContacts.find(c => c.id === filterStakeholder)?.name}
              </span>
            )}
            {filterTeamLead && (
              <span style={{ 
                padding: '4px 10px', 
                background: '#f0f4ff', 
                borderRadius: '12px', 
                fontSize: '0.75rem',
                color: '#667eea',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <Users size={10} />
                {sortedContacts.find(c => c.id === filterTeamLead)?.name}
              </span>
            )}
            <button
              onClick={() => { setFilterAgency(''); setFilterClient(''); setFilterStakeholder(''); setFilterTeamLead(''); }}
              style={{
                padding: '4px 8px',
                background: 'transparent',
                border: 'none',
                color: '#dc2626',
                fontSize: '0.75rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <X size={12} /> Clear
            </button>
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div style={{ padding: '24px 48px', overflowY: 'auto', flex: 1 }}>
        {/* Awaiting Execution Section */}
        {awaitingExecutionItems.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#7c3aed'
              }}>
                Cards awaiting execution from another team
              </h2>
              <span style={{
                padding: '2px 10px',
                background: '#f3e8ff',
                color: '#7c3aed',
                borderRadius: '12px',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}>
                {awaitingExecutionItems.length}
              </span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {awaitingExecutionItems.map((item, idx) => (
                <ItemCard key={item.id} item={item} index={idx} />
              ))}
            </div>
          </div>
        )}

        {/* Requested Section */}
        {requestedItems.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#92400e'
              }}>
                Requested
              </h2>
              <span style={{
                padding: '2px 10px',
                background: '#fef3c7',
                color: '#92400e',
                borderRadius: '12px',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}>
                {requestedItems.length}
              </span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {requestedItems.map((item, idx) => (
                <ItemCard key={item.id} item={item} index={idx} />
              ))}
            </div>
          </div>
        )}

        {/* On Going Section */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px', 
            marginBottom: '16px'
          }}>
            <h2 style={{ 
              margin: 0, 
              fontSize: '1.1rem', 
              fontWeight: '600', 
              color: '#1a1a2e' 
            }}>
              On going
            </h2>
            <span style={{
              padding: '2px 10px',
              background: '#dbeafe',
              color: '#2563eb',
              borderRadius: '12px',
              fontSize: '0.8rem',
              fontWeight: '600'
            }}>
              {ongoingItems.length}
            </span>
          </div>

          {ongoingItems.length === 0 ? (
            <div style={{
              padding: '24px',
              background: 'white',
              borderRadius: '12px',
              border: '1px dashed #e2e8f0',
              textAlign: 'center',
              color: '#94a3b8',
              fontSize: '0.9rem'
            }}>
              No items in progress. Drag items here or change status.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {ongoingItems.map((item, idx) => (
                <ItemCard key={item.id} item={item} index={idx} />
              ))}
            </div>
          )}
        </div>

        {/* Parking Lot Section */}
        <div style={{ 
          background: 'white', 
          borderRadius: '16px', 
          border: '1px solid #e2e8f0',
          padding: '24px',
          minHeight: '300px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px', 
            marginBottom: '20px'
          }}>
            <h2 style={{ 
              margin: 0, 
              fontSize: '1.1rem', 
              fontWeight: '600', 
              color: '#1a1a2e' 
            }}>
              Parking lot
            </h2>
            <span style={{
              padding: '2px 10px',
              background: '#f1f5f9',
              color: '#64748b',
              borderRadius: '12px',
              fontSize: '0.8rem',
              fontWeight: '600'
            }}>
              {parkingLotItems.length}
            </span>
          </div>

          {parkingLotItems.length === 0 ? (
            <div style={{
              padding: '48px',
              border: '1px dashed #e2e8f0',
              borderRadius: '12px',
              textAlign: 'center',
              color: '#94a3b8',
              fontSize: '0.9rem'
            }}>
              No items in the parking lot. Add new items to get started.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {parkingLotItems.map((item, idx) => (
                <ItemCard key={item.id} item={item} index={idx} />
              ))}
            </div>
          )}
        </div>

        {/* Done Section (collapsed) */}
        {doneItems.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              marginBottom: '16px'
            }}>
              <h2 style={{ 
                margin: 0, 
                fontSize: '1rem', 
                fontWeight: '600', 
                color: '#94a3b8' 
              }}>
                Done
              </h2>
              <span style={{
                padding: '2px 10px',
                background: '#dcfce7',
                color: '#16a34a',
                borderRadius: '12px',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}>
                {doneItems.length}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', opacity: 0.7 }}>
              {doneItems.map((item, idx) => (
                <ItemCard key={item.id} item={item} index={idx} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e8ecf1',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                {editingItem ? 'Edit Item' : 'Add New Item'}
              </h2>
              <button
                onClick={resetForm}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Enter item title"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '0.9rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Enter description (optional)"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    <Briefcase size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    Agency
                  </label>
                  <select
                    value={form.agency_id}
                    onChange={(e) => setForm({ ...form, agency_id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      background: 'white'
                    }}
                  >
                    <option value="">Select agency...</option>
                    {sortedAgencies.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    <Building2 size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    Client
                  </label>
                  <select
                    value={form.people_client_id}
                    onChange={(e) => setForm({ ...form, people_client_id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      background: 'white'
                    }}
                  >
                    <option value="">Select client...</option>
                    {sortedClients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    <User size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    Stakeholder
                  </label>
                  <select
                    value={form.stakeholder_id}
                    onChange={(e) => setForm({ ...form, stakeholder_id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      background: 'white'
                    }}
                  >
                    <option value="">Select stakeholder...</option>
                    {sortedContacts.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    <Users size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    Team Lead
                  </label>
                  <select
                    value={form.team_lead_id}
                    onChange={(e) => setForm({ ...form, team_lead_id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      background: 'white'
                    }}
                  >
                    <option value="">Select team lead...</option>
                    {sortedContacts.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  <FileSpreadsheet size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  Linked Sheet
                </label>
                <select
                  value={form.sheet_id}
                  onChange={(e) => setForm({ ...form, sheet_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: form.sheet_id ? '2px solid #667eea' : '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    background: form.sheet_id ? '#f0f4ff' : 'white'
                  }}
                >
                  <option value="">None</option>
                  {sheets.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}{s.creator_name ? ` (by ${s.creator_name})` : ''}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    <Flag size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    Priority
                  </label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      background: 'white'
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    Status
                  </label>
                  <select
                    value={form.status}
                    disabled={editingItem && (editingItem.parent_card_id != null || editingItem.status === 'awaiting_execution')}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      background: 'white'
                    }}
                  >
                    {form.status === 'awaiting_execution' && (
                      <option value="awaiting_execution">Awaiting Execution</option>
                    )}
                    <option value="requested">Requested</option>
                    <option value="backlog">Backlog</option>
                    <option value="in_progress">In Progress</option>
                    <option value="done">Done</option>
                  </select>
                </div>
              </div>

              {editingItem && editingItem.parent_card_id == null && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    Send to Workspace
                  </label>
                  <select
                    value={form.send_to_workspace_id || ''}
                    onChange={(e) => setForm({ ...form, send_to_workspace_id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      background: 'white'
                    }}
                  >
                    <option value="">Choose another workspace...</option>
                    {workspaces
                      .filter(ws => String(ws.id) !== String(workspaceId))
                      .map(ws => (
                        <option key={ws.id} value={ws.id}>{ws.name}</option>
                      ))
                    }
                  </select>
                </div>
              )}

              {/* Icon selector */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Card Icon
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, icon: '' })}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '8px',
                      border: !form.icon ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                      background: !form.icon ? '#f5f3ff' : '#fff',
                      color: '#6b7280',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      fontWeight: !form.icon ? 600 : 400
                    }}
                  >
                    None
                  </button>
                  {Object.entries(CARD_ICONS).map(([key, cfg]) => {
                    const isActive = form.icon === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setForm({ ...form, icon: key })}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '8px',
                          border: isActive ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                          background: isActive ? '#f5f3ff' : '#fff',
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontWeight: isActive ? 600 : 400,
                          color: '#374151'
                        }}
                      >
                        <span style={{ fontSize: '1.1rem' }}>{cfg.emoji}</span>
                        {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '10px 20px',
                    background: '#f1f5f9',
                    color: '#64748b',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <Check size={16} />
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Return Observations Modal */}
      {showReturnModal && returnItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '450px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e8ecf1',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1f2937' }}>
                Return Card with Observations
              </h2>
              <button
                onClick={() => {
                  setShowReturnModal(false);
                  setReturnItem(null);
                  setReturnObservations('');
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              if (returnObservations.trim().length < 10) {
                alert('Observations must be at least 10 characters long.');
                return;
              }
              await handleReturn(returnItem.id, 'returned_with_observations', returnObservations);
            }} style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '0.85rem', color: '#4b5563', margin: '0 0 12px' }}>
                  Returning: <strong style={{ color: '#111827' }}>{returnItem.title}</strong>
                </p>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Observations / Notes (min 10 chars) *
                </label>
                <textarea
                  value={returnObservations}
                  onChange={(e) => setReturnObservations(e.target.value)}
                  placeholder="Explain why this card is being returned or what needs attention..."
                  rows={4}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowReturnModal(false);
                    setReturnItem(null);
                    setReturnObservations('');
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#f1f5f9',
                    color: '#64748b',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <Check size={16} />
                  Submit Return
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Planner;
